/**
 * Full notifications inbox (spec §12.5): filters by read-state and event
 * type, dismiss, mark-all-read, infinite scroll via "بیشتر".
 */
import { useMemo, useState } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import { Bell, CheckCheck, Eye } from 'lucide-react'
import { PageHeader } from '@/components/shared/page-header'
import { Button } from '@/components/ui/button'
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils/cn'
import {
  useNotificationsInfiniteQuery,
  useUnreadCountQuery,
  useMarkReadMutation,
  useMarkAllReadMutation,
  useDismissMutation,
  useEventCatalogQuery,
} from '../hooks/use-notifications'
import { configFor } from '../lib/notification-catalog'
import { NotificationItem } from './../components/notification-item'
import type { NotificationRow } from '../api/notifications-api'

export function NotificationsPage() {
  const { projectId } = useParams<{ projectId: string }>()
  const navigate = useNavigate()

  const [tab, setTab] = useState<'all' | 'unread'>('all')
  const [eventType, setEventType] = useState<string>('all')
  const [showDismissed, setShowDismissed] = useState(false)

  const { data: unread = 0 } = useUnreadCountQuery(projectId)
  const { data: catalog = [] } = useEventCatalogQuery()
  const feed = useNotificationsInfiniteQuery(projectId, {
    unreadOnly: tab === 'unread',
    eventType: eventType === 'all' ? undefined : eventType,
    includeDismissed: showDismissed,
    pageSize: 50,
  })
  const markRead = useMarkReadMutation(projectId)
  const markAll = useMarkAllReadMutation(projectId)
  const dismiss = useDismissMutation(projectId)

  const catalogLabels = useMemo(
    () => new Map(catalog.map((t) => [t.event_type, t.label_fa])),
    [catalog],
  )

  const items = feed.data?.pages.flat() ?? []

  const openNotification = (n: NotificationRow) => {
    if (!n.is_read) markRead.mutate(n.id)
    const route = configFor(n.event_type).route(n)
    if (route) navigate(route)
  }

  return (
    <div className="flex h-full flex-col">
      <PageHeader
        title="اعلان‌ها"
        breadcrumbs={[{ label: 'اعلان‌ها' }]}
        actions={
          <Button
            variant="outline"
            size="sm"
            className="gap-1.5"
            disabled={unread === 0 || markAll.isPending}
            onClick={() => markAll.mutate()}
          >
            <CheckCheck className="h-4 w-4" />
            خواندن همه ({unread})
          </Button>
        }
      />

      <div className="flex-1 overflow-y-auto p-6" dir="rtl">
        <div className="mx-auto max-w-3xl space-y-4">
          <div className="flex flex-wrap items-center gap-3">
            <Tabs value={tab} onValueChange={(v) => setTab(v as 'all' | 'unread')} dir="rtl">
              <TabsList>
                <TabsTrigger value="all" className="text-xs">
                  همه
                </TabsTrigger>
                <TabsTrigger value="unread" className="text-xs">
                  خوانده‌نشده {unread > 0 && `(${unread})`}
                </TabsTrigger>
              </TabsList>
            </Tabs>

            <Select value={eventType} onValueChange={setEventType} dir="rtl">
              <SelectTrigger className="h-9 w-56 text-xs">
                <SelectValue placeholder="نوع رویداد" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all" className="text-xs">
                  همه رویدادها
                </SelectItem>
                {catalog.map((t) => (
                  <SelectItem key={t.event_type} value={t.event_type} className="text-xs">
                    {t.label_fa}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Button
              variant="ghost"
              size="sm"
              className={cn('gap-1.5 text-xs', showDismissed && 'bg-muted text-foreground')}
              onClick={() => setShowDismissed((v) => !v)}
            >
              <Eye className="h-3.5 w-3.5" />
              نمایش حذف‌شده‌ها
            </Button>
          </div>

          <div className="rounded-lg border border-border bg-card">
            {feed.isLoading ? (
              <div className="space-y-2 p-4">
                {Array.from({ length: 5 }).map((_, i) => (
                  <div key={i} className="h-14 animate-pulse rounded bg-muted" />
                ))}
              </div>
            ) : items.length === 0 ? (
              <div className="flex flex-col items-center gap-2 py-16 text-muted-foreground">
                <Bell className="h-8 w-8" />
                <span className="text-sm">
                  {tab === 'unread' ? 'اعلان خوانده‌نشده‌ای ندارید' : 'اعلانی ندارید'}
                </span>
              </div>
            ) : (
              items.map((n) => (
                <NotificationItem
                  key={n.id}
                  notification={n}
                  catalogLabelFa={catalogLabels.get(n.event_type)}
                  onOpen={openNotification}
                  onDismiss={(row) => dismiss.mutate(row.id)}
                />
              ))
            )}
          </div>

          {feed.hasNextPage && (
            <div className="flex justify-center">
              <Button
                variant="outline"
                size="sm"
                disabled={feed.isFetchingNextPage}
                onClick={() => feed.fetchNextPage()}
              >
                {feed.isFetchingNextPage ? 'در حال بارگذاری…' : 'موارد بیشتر'}
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
